import React, { useMemo, useState } from "react";
import LineChart from "../../shared/components/Charts/LineChart";
import { DEMO_DATES, DEMO_LOGS } from "../data/operationsDemoData";
import "./styling/OperationalDataDemo.css";

/**
 * OperationalDataDemo — homepage marketing preview of the Operational Data
 * page. Shows the same two views the real page offers (spreadsheet table and
 * time-series chart) over illustrative logs, so the "define your own columns"
 * idea is visible without an account.
 */

/* Enough rows to fill the panel's min-height, which is set by the taller chart
   view so toggling between the two doesn't resize the section. */
const TABLE_ROWS = 11;

const fmt = (v, decimals) =>
  v === null || v === undefined ? "—" : v.toFixed(decimals);

const shortDate = (iso) => iso.slice(5).replace("-", "/");

const OperationalDataDemo = () => {
  const [logId, setLogId] = useState(DEMO_LOGS[0].id);
  const [view, setView] = useState("table");

  const log = useMemo(
    () => DEMO_LOGS.find((l) => l.id === logId) || DEMO_LOGS[0],
    [logId]
  );

  // First two number columns are preselected, matching the real chart panel.
  const [selected, setSelected] = useState(() =>
    DEMO_LOGS[0].columns.slice(0, 2).map((c) => c.key)
  );

  const switchLog = (id) => {
    const next = DEMO_LOGS.find((l) => l.id === id);
    setLogId(id);
    setSelected(next.columns.slice(0, 2).map((c) => c.key));
  };

  const toggleColumn = (key) =>
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  /** Blank a few cells so the table shows the real "—" empty rendering. */
  const valueAt = (col, rowIdx) =>
    (log.sparse?.[col.key] || []).includes(rowIdx) ? null : col.values[rowIdx];

  const chartData = useMemo(() => {
    const cols = log.columns.filter((c) => selected.includes(c.key));
    return {
      labels: DEMO_DATES.map(shortDate),
      datasets: cols.map((c) => ({
        label: `${c.label} (${c.unit})`,
        data: DEMO_DATES.map((_, i) => valueAt(c, i)),
        borderColor: c.color,
        backgroundColor: `${c.color}22`,
        tension: 0.15,
        borderWidth: 2,
        pointRadius: 0,
        spanGaps: true,
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log, selected]);

  const chartOptions = useMemo(
    () => ({
      scales: {
        x: {
          ticks: {
            color: "#B6B6DE",
            font: { family: "'Inter', sans-serif", size: 9 },
            maxTicksLimit: 8,
          },
          grid: { color: "rgba(155,155,210,0.10)" },
        },
        y: {
          ticks: { color: "#B6B6DE", font: { family: "'Inter', sans-serif", size: 9 } },
          grid: { color: "rgba(155,155,210,0.10)" },
        },
      },
      plugins: { legend: { display: false } },
    }),
    []
  );

  // Most recent rows first — the real table's default reading order.
  const tableRows = useMemo(() => {
    const start = DEMO_DATES.length - TABLE_ROWS;
    return DEMO_DATES.slice(start)
      .map((date, i) => ({ date, idx: start + i }))
      .reverse();
  }, []);

  return (
    <div className="opdata-demo">
      <div className="opdata-demo__toolbar">
        <div className="opdata-demo__logs" role="group" aria-label="Select log">
          {DEMO_LOGS.map((l) => (
            <button
              key={l.id}
              type="button"
              className={`opdata-demo__log-btn${l.id === logId ? " is-active" : ""}`}
              onClick={() => switchLog(l.id)}
              aria-pressed={l.id === logId}
            >
              📒 {l.name}
            </button>
          ))}
        </div>
        <div className="opdata-demo__views" role="group" aria-label="View mode">
          {["table", "chart"].map((v) => (
            <button
              key={v}
              type="button"
              className={`opdata-demo__view-btn${view === v ? " is-active" : ""}`}
              onClick={() => setView(v)}
              aria-pressed={view === v}
            >
              {v === "table" ? "Table" : "Chart"}
            </button>
          ))}
        </div>
      </div>

      <p className="opdata-demo__meta">
        {DEMO_DATES.length} entries · {DEMO_DATES[0]} → {DEMO_DATES[DEMO_DATES.length - 1]}
      </p>

      {view === "table" ? (
        <div className="opdata-demo__table-wrap">
          <table className="opdata-demo__table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                {log.columns.map((c) => (
                  <th key={c.key} scope="col">
                    <span className="opdata-demo__col-label">{c.label}</span>
                    <span className="opdata-demo__col-unit">{c.unit}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.date}>
                  <th scope="row">{row.date}</th>
                  {log.columns.map((c) => {
                    const v = valueAt(c, row.idx);
                    return (
                      <td key={c.key} className={v === null ? "is-empty" : undefined}>
                        {fmt(v, c.decimals)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="opdata-demo__chart-panel">
          <div className="opdata-demo__series" role="group" aria-label="Plotted columns">
            {log.columns.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`opdata-demo__series-btn${
                  selected.includes(c.key) ? " is-active" : ""
                }`}
                style={{ "--series-color": c.color }}
                onClick={() => toggleColumn(c.key)}
                aria-pressed={selected.includes(c.key)}
              >
                <span className="opdata-demo__swatch" />
                {c.label}
              </button>
            ))}
          </div>
          <div className="opdata-demo__chart">
            {selected.length ? (
              <LineChart
                data={chartData}
                height="100%"
                showLegend={false}
                customOptions={chartOptions}
              />
            ) : (
              <p className="opdata-demo__chart-empty">
                Select a column to plot it.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalDataDemo;
